import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { createPortal } from "react-dom"
import { motion, AnimatePresence } from "framer-motion"
import {
  listContentTypes,
  getContentType,
  createContentType,
  updateContentType,
  deleteContentType,
  listEntriesBySlug,
  upsertEntryBySlug,
  updateEntry,
  publishEntry,
  unpublishEntry,
  deleteEntry,
} from "../api/contentApi"
import { Badge, Btn } from "../components/ui"
import MediaLibraryModal from "../components/MediaLibraryModal"
import {
  Plus,
  PencilSimple,
  TrashSimple,
  FolderSimple,
  FileText,
  MagnifyingGlass,
  X,
  ArrowSquareOut,
  CaretDown,
  Check,
  LinkSimple,
  Image as ImageIcon,
} from "phosphor-react"
import RichTextEditor from "../components/RichTextEditor" // adjust path

/**
 * Content Studio — Clean / Professional / Chic
 * ✅ Bullet-proof fixes:
 * - No hooks inside conditional branches (JSON editor extracted component)
 * - React is imported (PortalSelect uses React.Children)
 * - Safer async calls + error surfaces
 * - Symmetrical layout + consistent spacing
 * - Defensive guards for null/undefined / schema edge-cases
 */

/* -------------------------------- Helpers -------------------------------- */

const ease = [0.16, 1, 0.3, 1]

function cx(...a) {
  return a.filter(Boolean).join(" ")
}

function isObj(v) {
  return v && typeof v === "object" && !Array.isArray(v)
}

function normalizeSlug(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-_]/g, "")
}

function splitPageSection(slug) {
  const s = String(slug || "")
  const idx = s.indexOf("-")
  if (idx === -1) return { page: "global", section: s }
  return { page: s.slice(0, idx), section: s.slice(idx + 1) }
}

function ensureLocalized(value, locales, fallback = "") {
  if (isObj(value)) return value
  const out = {}
  locales.forEach((l) => (out[l] = value ?? fallback))
  return out
}

function ensureLocalizedObject(value, locales, fallbackObj) {
  if (isObj(value) && Object.keys(value).some((k) => locales.includes(k))) return value
  const out = {}
  locales.forEach((l) => (out[l] = isObj(value) ? value : fallbackObj))
  return out
}

function safeJsonParse(text) {
  try {
    return { ok: true, value: JSON.parse(text) }
  } catch (e) {
    return { ok: false, error: e?.message || "Invalid JSON" }
  }
}

function formatDateInput(value) {
  if (!value) return ""
  const s = String(value)
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return ""
  const yyyy = String(d.getFullYear())
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

/* -------------------------------- Storage -------------------------------- */

const LS_PAGES_KEY = "cms_pages_v4"

function readSavedPagesSafe() {
  try {
    if (typeof window === "undefined") return []
    const raw = window.localStorage.getItem(LS_PAGES_KEY)
    const arr = JSON.parse(raw || "[]")
    return Array.isArray(arr) ? arr : []
  } catch {
    return []
  }
}

function writeSavedPagesSafe(pages) {
  try {
    if (typeof window === "undefined") return
    window.localStorage.setItem(LS_PAGES_KEY, JSON.stringify(pages || []))
  } catch {
    // ignore
  }
}

/* -------------------------------- Constants -------------------------------- */

const FIELD_TYPES = [
  "string",
  "text",
  "richtext",
  "number",
  "boolean",
  "enum",
  "date",
  "media",
  "json",
  "repeater",
  "link",
]

const emptyField = () => ({
  key: "",
  label: "",
  type: "string",
  required: false,
  localized: false,

  // number
  min: undefined,
  max: undefined,

  // string/text
  regex: "",

  // enum
  values: [],

  // media
  multiple: false,

  // repeater
  fields: [],

  // link options
  link_with_image: true,
})

const emptyLinkValue = () => ({ label: "", url: "", image: "" })

/* -------------------------------- Motion -------------------------------- */

const fade = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.18, ease } },
  exit: { opacity: 0, transition: { duration: 0.16, ease } },
}

const pop = {
  initial: { opacity: 0, scale: 0.985, y: 10 },
  animate: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.22, ease } },
  exit: { opacity: 0, scale: 0.985, y: 10, transition: { duration: 0.16, ease } },
}

const listItem = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.18, ease } },
  exit: { opacity: 0, y: 8, transition: { duration: 0.14, ease } },
}

/* -------------------------------- UI Atoms -------------------------------- */

function IconButton({ title, onClick, children, tone = "neutral", disabled = false }) {
  const base =
    "h-9 w-9 rounded-2xl grid place-items-center border transition active:scale-[0.98] outline-none"
  const tones = {
    neutral: "border-neutral-200 bg-white hover:border-neutral-900",
    dark: "border-neutral-900 bg-neutral-900 text-white hover:bg-neutral-800",
    danger: "border-red-200 bg-red-50 text-red-700 hover:border-red-400",
    cyan: "border-cyan-200 bg-cyan-50 text-cyan-700 hover:border-cyan-400",
  }
  return (
    <motion.button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      whileHover={!disabled ? { y: -1 } : undefined}
      whileTap={!disabled ? { scale: 0.98 } : undefined}
      className={cx(base, tones[tone] || tones.neutral, disabled && "opacity-50 cursor-not-allowed")}
    >
      {children}
    </motion.button>
  )
}

function SearchInput({ value, onChange, placeholder }) {
  return (
    <div className="h-10 w-full rounded-2xl border border-neutral-200 bg-white flex items-center gap-2 px-3 focus-within:border-neutral-900 transition">
      <MagnifyingGlass size={16} className="text-neutral-500" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-full w-full text-sm outline-none bg-transparent"
      />
      {value ? (
        <button
          type="button"
          onClick={() => onChange("")}
          className="text-neutral-500 hover:text-neutral-900"
          title="Clear"
        >
          <X size={16} />
        </button>
      ) : null}
    </div>
  )
}

function Label({ children }) {
  return <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500">{children}</div>
}

function Input({ value, onChange, placeholder, type = "text" }) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="mt-2 h-10 w-full rounded-2xl border border-neutral-200 bg-white px-3 text-sm outline-none focus:border-neutral-900 transition"
    />
  )
}

function Textarea({ value, onChange, rows = 4, placeholder }) {
  return (
    <textarea
      value={value}
      onChange={onChange}
      rows={rows}
      placeholder={placeholder}
      className="mt-2 w-full rounded-2xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-neutral-900 transition"
    />
  )
}

export function Checkbox({ checked, onChange, label, disabled = false }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => !disabled && onChange?.(!checked)}
      className={cx(
        "flex items-center gap-3 select-none",
        disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
      )}
    >
      <span
        className={cx(
          "h-5 w-5 rounded-lg border grid place-items-center transition",
          checked
            ? "bg-neutral-900 border-neutral-900 text-white"
            : "bg-white border-neutral-300 text-transparent"
        )}
      >
        <Check size={14} weight="bold" />
      </span>
      {label ? <span className="text-sm font-medium text-neutral-900">{label}</span> : null}
    </button>
  )
}

export function Switch({ checked, onChange, label, disabled = false }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => !disabled && onChange?.(!checked)}
      className={cx(
        "h-10 px-3 rounded-2xl border flex items-center gap-3 transition select-none outline-none",
        disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:border-neutral-900",
        checked
          ? "bg-neutral-900 border-neutral-900 text-white"
          : "bg-white border-neutral-200 text-neutral-900"
      )}
    >
      <span className={cx("relative w-10 h-6 rounded-full transition", checked ? "bg-white/20" : "bg-neutral-100")}>
        <span
          className={cx(
            "absolute top-1/2 -translate-y-1/2 h-4 w-4 rounded-full transition",
            checked ? "right-1 bg-white" : "left-1 bg-neutral-900"
          )}
        />
      </span>
      {label ? <span className="text-sm font-medium">{label}</span> : null}
    </button>
  )
}

/* ----------------------------- Portal Select ----------------------------- */

function getOptionsFromChildren(children) {
  const arr = []
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return
    const v = child.props?.value ?? ""
    const label =
      typeof child.props?.children === "string"
        ? child.props.children
        : String(child.props?.children ?? v)
    arr.push({ value: String(v), label })
  })
  return arr
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n))
}

function PortalSelect({
  value,
  onChange,
  children,
  disabled = false,
  placeholder = "Select...",
  className = "",
  menuMaxHeight = 320,
}) {
  const options = useMemo(() => getOptionsFromChildren(children), [children])
  const selectedIndex = Math.max(0, options.findIndex((o) => String(o.value) === String(value)))

  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(selectedIndex)

  const btnRef = useRef(null)
  const menuRef = useRef(null)
  const rafRef = useRef(null)

  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 })

  const selected = options.find((o) => String(o.value) === String(value))
  const emit = (nextValue) => onChange?.({ target: { value: nextValue } })

  const computePosition = useCallback(() => {
    const el = btnRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const gap = 8
    const viewportH = window.innerHeight || 0
    const estimated = Math.min(menuMaxHeight, options.length * 40 + 12)
    const spaceBelow = viewportH - r.bottom - gap
    const spaceAbove = r.top - gap
    const dropUp = spaceBelow < estimated && spaceAbove > spaceBelow
    const top = dropUp ? Math.max(gap, r.top - gap - estimated) : r.bottom + gap

    setPos({
      top: Math.round(top),
      left: Math.round(r.left),
      width: Math.round(r.width),
    })
  }, [menuMaxHeight, options.length])

  useLayoutEffect(() => {
    if (!open) return
    computePosition()
    rafRef.current = requestAnimationFrame(() => menuRef.current?.focus?.())
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [open, computePosition])

  useEffect(() => {
    if (!open) return
    const onResize = () => computePosition()
    const onScroll = () => computePosition()
    window.addEventListener("resize", onResize)
    window.addEventListener("scroll", onScroll, true)
    return () => {
      window.removeEventListener("resize", onResize)
      window.removeEventListener("scroll", onScroll, true)
    }
  }, [open, computePosition])

  useEffect(() => {
    if (!open) return
    const onDown = (e) => {
      const t = e.target
      if (btnRef.current?.contains(t)) return
      if (menuRef.current?.contains(t)) return
      setOpen(false)
    }
    window.addEventListener("mousedown", onDown)
    return () => window.removeEventListener("mousedown", onDown)
  }, [open])

  useEffect(() => {
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0)
  }, [selectedIndex])

  useEffect(() => {
    if (!open) return
    const el = menuRef.current?.querySelector?.(`[data-idx="${activeIndex}"]`)
    el?.scrollIntoView?.({ block: "nearest" })
  }, [activeIndex, open])

  const onButtonKeyDown = (e) => {
    if (disabled) return
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      setOpen((p) => !p)
    }
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setOpen(true)
      setActiveIndex((i) => clamp(i + 1, 0, options.length - 1))
    }
    if (e.key === "ArrowUp") {
      e.preventDefault()
      setOpen(true)
      setActiveIndex((i) => clamp(i - 1, 0, options.length - 1))
    }
  }

  const onMenuKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault()
      setOpen(false)
      btnRef.current?.focus?.()
    }
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActiveIndex((i) => clamp(i + 1, 0, options.length - 1))
    }
    if (e.key === "ArrowUp") {
      e.preventDefault()
      setActiveIndex((i) => clamp(i - 1, 0, options.length - 1))
    }
    if (e.key === "Enter") {
      e.preventDefault()
      const opt = options[activeIndex]
      if (opt) {
        emit(opt.value)
        setOpen(false)
        btnRef.current?.focus?.()
      }
    }
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((p) => !p)}
        onKeyDown={onButtonKeyDown}
        className={cx(
          "relative h-10 w-full rounded-2xl border px-3 pr-10 text-sm text-left transition outline-none",
          disabled
            ? "opacity-60 cursor-not-allowed bg-neutral-50 border-neutral-200"
            : "bg-white hover:border-neutral-900",
          open ? "border-neutral-900" : "border-neutral-200",
          className
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={cx("block truncate", selected ? "text-neutral-900" : "text-neutral-400")}>
          {selected?.label || placeholder}
        </span>
        <span
          className={cx(
            "absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 transition",
            open && "rotate-180"
          )}
        >
          <CaretDown size={16} />
        </span>
      </button>

      {open &&
        createPortal(
          <div style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width, zIndex: 99999 }}>
            <div
              ref={menuRef}
              tabIndex={-1}
              role="listbox"
              onKeyDown={onMenuKeyDown}
              className="rounded-2xl border border-neutral-200 bg-white shadow-[0_24px_70px_rgba(0,0,0,0.14)] outline-none overflow-hidden"
            >
              <div className="p-1 overflow-auto" style={{ maxHeight: menuMaxHeight }}>
                {options.map((o, idx) => {
                  const isSelected = String(o.value) === String(value)
                  const isActive = idx === activeIndex
                  return (
                    <button
                      key={`${o.value}-${idx}`}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      data-idx={idx}
                      onMouseEnter={() => setActiveIndex(idx)}
                      onClick={() => {
                        emit(o.value)
                        setOpen(false)
                        btnRef.current?.focus?.()
                      }}
                      className={cx(
                        "w-full rounded-xl px-3 py-2 text-sm text-left flex items-center justify-between gap-3 transition",
                        isActive ? "bg-neutral-100" : "bg-white",
                        "hover:bg-neutral-100"
                      )}
                    >
                      <span className="truncate text-neutral-900">{o.label}</span>
                      {isSelected ? (
                        <span className="text-neutral-900">
                          <Check size={16} weight="bold" />
                        </span>
                      ) : null}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  )
}

/* -------------------------------- Layout -------------------------------- */

function Panel({ title, icon, right, children, className = "" }) {
  return (
    <motion.div
      variants={pop}
      initial="initial"
      animate="animate"
      className={cx("rounded-3xl border border-neutral-200 bg-white overflow-hidden", className)}
    >
      <div className="px-4 py-3 border-b border-neutral-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-neutral-800">{icon}</span>
          <div className="text-sm font-semibold text-neutral-900 truncate">{title}</div>
        </div>
        <div className="flex items-center gap-2">{right}</div>
      </div>
      <div className="p-4">{children}</div>
    </motion.div>
  )
}

function Modal({ open, title, icon, onClose, children, max = "max-w-3xl" }) {
  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-50 grid place-items-center p-4"
          initial="initial"
          animate="animate"
          exit="exit"
        >
          <motion.div variants={fade} className="absolute inset-0 bg-black/45" onClick={onClose} />
          <motion.div
            variants={pop}
            className={cx("relative w-full rounded-[28px] border border-neutral-200 bg-white shadow-2xl", max)}
          >
            <div className="px-4 py-3 border-b border-neutral-100 flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-neutral-800">{icon}</span>
                <div className="text-sm font-semibold text-neutral-900 truncate">{title}</div>
              </div>
              <IconButton title="Close" onClick={onClose}>
                <X size={16} />
              </IconButton>
            </div>
            <div className="p-4">{children}</div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}

function ConfirmModal({ open, title, description, danger = false, onCancel, onConfirm }) {
  return (
    <Modal open={open} onClose={onCancel} title={title || "Confirm"} icon={<TrashSimple size={18} />} max="max-w-lg">
      <div className="space-y-4">
        <div className="text-sm text-neutral-700 leading-6">{description}</div>
        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={onCancel}>
            Cancel
          </Btn>
          <Btn variant={danger ? "danger" : undefined} onClick={onConfirm}>
            Confirm
          </Btn>
        </div>
      </div>
    </Modal>
  )
}

function MiniCard({ label, value }) {
  return (
    <div className="rounded-3xl border border-neutral-200 bg-white p-3">
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500">{label}</div>
      <div className="mt-1 text-sm text-neutral-900 truncate">{value}</div>
    </div>
  )
}

function ErrorHint({ text }) {
  if (!text) return null
  return (
    <div className="rounded-3xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
      {text}
    </div>
  )
}

/* ------------------------------ Main Page ------------------------------ */

export default function ContentStudioOnePage() {
  const [types, setTypes] = useState([])
  const [loadingTypes, setLoadingTypes] = useState(true)
  const [typesErr, setTypesErr] = useState("")

  // pages
  const [activePage, setActivePage] = useState(null)
  const [pageQ, setPageQ] = useState("")
  const [sectionQ, setSectionQ] = useState("")

  // active section
  const [activeSectionSlug, setActiveSectionSlug] = useState(null)
  const activeType = useMemo(
    () => (types || []).find((t) => t?.slug === activeSectionSlug) || null,
    [types, activeSectionSlug]
  )

  // entries
  const [entries, setEntries] = useState([])
  const [loadingEntries, setLoadingEntries] = useState(false)
  const [entriesErr, setEntriesErr] = useState("")

  // selection
  const [selected, setSelected] = useState(() => new Set())

  // modals
  const [createPageOpen, setCreatePageOpen] = useState(false)
  const [createSectionOpen, setCreateSectionOpen] = useState(false)

  const [schemaOpen, setSchemaOpen] = useState(false)
  const [schemaMode, setSchemaMode] = useState("create")
  const [schemaId, setSchemaId] = useState(null)
  const schemaPreloadRef = useRef(null)

  const [entryOpen, setEntryOpen] = useState(false)
  const [entryMode, setEntryMode] = useState("create")
  const [entryId, setEntryId] = useState(null)

  const [confirm, setConfirm] = useState({ open: false, title: "", description: "", danger: true, action: null })
  const openConfirm = useCallback(
    ({ title, description, danger = true, action }) => setConfirm({ open: true, title, description, danger, action }),
    []
  )

  // media modal
  const [mediaOpen, setMediaOpen] = useState(false)
  const [mediaCtx, setMediaCtx] = useState(null)

  const loadTypes = useCallback(async () => {
    setLoadingTypes(true)
    setTypesErr("")
    try {
      const res = await listContentTypes()
      setTypes(res?.data?.data || [])
    } catch (e) {
      setTypesErr(e?.message || "Failed to load content types.")
      setTypes([])
    } finally {
      setLoadingTypes(false)
    }
  }, [])

  useEffect(() => {
    loadTypes()
  }, [loadTypes])

  const pages = useMemo(() => {
    const saved = readSavedPagesSafe()
    const derived = new Map()
      ; (types || []).forEach((t) => {
        const { page } = splitPageSection(t?.slug)
        if (!derived.has(page)) derived.set(page, { slug: page, name: page })
      })

    const merged = new Map()
    saved.forEach((p) => merged.set(p.slug, p))
    derived.forEach((p) => {
      if (!merged.has(p.slug)) merged.set(p.slug, p)
    })

    const arr = Array.from(merged.values())
    arr.sort((a, b) => (a?.name || a?.slug || "").localeCompare(b?.name || b?.slug || ""))
    return arr
  }, [types])

  useEffect(() => {
    if (activePage) return
    if (pages.length) setActivePage(pages[0].slug)
  }, [pages, activePage])

  const sections = useMemo(() => {
    if (!activePage) return []
    const list = (types || []).filter((t) => splitPageSection(t?.slug).page === activePage)
    list.sort((a, b) => (a?.name || a?.slug || "").localeCompare(b?.name || b?.slug || ""))
    return list
  }, [types, activePage])

  useEffect(() => {
    if (!activePage) return
    if (sections.some((s) => s?.slug === activeSectionSlug)) return
    setActiveSectionSlug(sections?.[0]?.slug || null)
    setSelected(new Set())
  }, [activePage, sections, activeSectionSlug])

  const pagesFiltered = useMemo(() => {
    const q = pageQ.trim().toLowerCase()
    if (!q) return pages
    return pages.filter((p) => (p.name || p.slug || "").toLowerCase().includes(q) || String(p.slug || "").toLowerCase().includes(q))
  }, [pages, pageQ])

  const sectionsFiltered = useMemo(() => {
    const q = sectionQ.trim().toLowerCase()
    if (!q) return sections
    return sections.filter((s) => (s.name || s.slug || "").toLowerCase().includes(q) || String(s.slug || "").toLowerCase().includes(q))
  }, [sections, sectionQ])

  const pageCounts = useMemo(() => {
    const map = new Map()
      ; (types || []).forEach((t) => {
        const { page } = splitPageSection(t?.slug)
        map.set(page, (map.get(page) || 0) + 1)
      })
    return map
  }, [types])

  const refreshEntries = useCallback(async (slug) => {
    if (!slug) {
      setEntries([])
      return
    }
    setLoadingEntries(true)
    setEntriesErr("")
    try {
      const res = await listEntriesBySlug(slug)
      setEntries(res?.data?.data || [])
    } catch (e) {
      setEntriesErr(e?.message || "Failed to load entries.")
      setEntries([])
    } finally {
      setLoadingEntries(false)
    }
  }, [])

  useEffect(() => {
    refreshEntries(activeType?.slug)
  }, [activeType?.slug, refreshEntries])

  const toggleSelect = useCallback((slug) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(slug)) next.delete(slug)
      else next.add(slug)
      return next
    })
  }, [])

  const clearSelection = useCallback(() => setSelected(new Set()), [])
  const selectAllVisible = useCallback(() => {
    setSelected((prev) => {
      const next = new Set(prev)
      sectionsFiltered.forEach((s) => s?.slug && next.add(s.slug))
      return next
    })
  }, [sectionsFiltered])

  const selectedList = useMemo(() => {
    const arr = Array.from(selected)
    return arr.map((slug) => (types || []).find((t) => t?.slug === slug)).filter(Boolean)
  }, [selected, types])

  const bulkDelete = useCallback(() => {
    if (!selectedList.length) return
    openConfirm({
      title: "Delete selected sections?",
      description: `This will delete ${selectedList.length} section(s) and their schemas.`,
      danger: true,
      action: async () => {
        try {
          for (const t of selectedList) {
            // eslint-disable-next-line no-await-in-loop
            await deleteContentType(t.id)
          }
          clearSelection()
          await loadTypes()
        } finally {
          setConfirm((p) => ({ ...p, open: false, action: null }))
        }
      },
    })
  }, [selectedList, openConfirm, clearSelection, loadTypes])

  const onCreatePage = useCallback(({ name, slug }) => {
    const s = normalizeSlug(slug || name)
    if (!s) return
    const saved = readSavedPagesSafe()
    const exists = saved.some((p) => p.slug === s)
    const next = exists
      ? saved.map((p) => (p.slug === s ? { ...p, name: name || p.name } : p))
      : [...saved, { slug: s, name: name || s }]
    writeSavedPagesSafe(next)
    setActivePage(s)
  }, [])

  const onCreateSection = useCallback(
    ({ sectionName, sectionSlug }) => {
      if (!activePage) return
      const sec = normalizeSlug(sectionSlug || sectionName)
      if (!sec) return
      const fullSlug = `${activePage}-${sec}`

      setSchemaMode("create")
      setSchemaId(null)
      schemaPreloadRef.current = { preName: sectionName || "", preSlug: fullSlug }
      setSchemaOpen(true)
    },
    [activePage]
  )

  const openEditSchema = useCallback((typeId) => {
    setSchemaMode("edit")
    setSchemaId(typeId)
    schemaPreloadRef.current = null
    setSchemaOpen(true)
  }, [])

  const onDeleteType = useCallback(
    (t) => {
      openConfirm({
        title: "Delete this section?",
        description: `Delete "${t?.name || t?.slug}"? This removes its schema.`,
        danger: true,
        action: async () => {
          try {
            await deleteContentType(t.id)
            if (activeSectionSlug === t.slug) setActiveSectionSlug(null)
            setSelected((prev) => {
              const next = new Set(prev)
              next.delete(t.slug)
              return next
            })
            await loadTypes()
          } finally {
            setConfirm((p) => ({ ...p, open: false, action: null }))
          }
        },
      })
    },
    [openConfirm, activeSectionSlug, loadTypes]
  )

  const openEntryEditor = useCallback((mode, id = null) => {
    setEntryMode(mode)
    setEntryId(id)
    setEntryOpen(true)
  }, [])

  const togglePublishEntry = useCallback(
    (entry) => {
      openConfirm({
        title: entry?.status === "published" ? "Unpublish entry?" : "Publish entry?",
        description:
          entry?.status === "published"
            ? "This entry will be hidden from the website."
            : "This entry will be visible on the website.",
        danger: false,
        action: async () => {
          try {
            if (entry?.status === "published") await unpublishEntry(entry.id)
            else await publishEntry(entry.id)
            await refreshEntries(activeType?.slug)
          } finally {
            setConfirm((p) => ({ ...p, open: false, action: null }))
          }
        },
      })
    },
    [openConfirm, refreshEntries, activeType?.slug]
  )

  const onDeleteEntry = useCallback(
    (entry) => {
      openConfirm({
        title: "Delete entry?",
        description: "This cannot be undone.",
        danger: true,
        action: async () => {
          try {
            await deleteEntry(entry.id)
            await refreshEntries(activeType?.slug)
          } finally {
            setConfirm((p) => ({ ...p, open: false, action: null }))
          }
        },
      })
    },
    [openConfirm, refreshEntries, activeType?.slug]
  )

  return (
    <motion.div
      className="p-5 text-start"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0, transition: { duration: 0.22, ease } }}
    >
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-[11px] tracking-[0.24em] uppercase text-neutral-500">CMS</div>
          <h1 className="mt-1 text-xl font-semibold text-neutral-900">Content Studio</h1>
          <p className="mt-1 text-sm text-neutral-600">Pages → Sections → Entries (clean, fast, and safe)</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Btn onClick={() => setCreatePageOpen(true)}>
            <span className="inline-flex items-center gap-2">
              <Plus size={18} weight="bold" /> Page
            </span>
          </Btn>

          <Btn variant="ghost" onClick={() => setCreateSectionOpen(true)} disabled={!activePage}>
            <span className="inline-flex items-center gap-2">
              <Plus size={18} weight="bold" /> Section
            </span>
          </Btn>
        </div>
      </div>

      <div className="mt-4">
        <ErrorHint text={typesErr} />
      </div>

      {/* Layout */}
      <div className="mt-5 grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Pages */}
        <Panel
          className="lg:col-span-3"
          title="Pages"
          icon={<FolderSimple size={18} />}
          right={
            <IconButton title="Create Page" tone="dark" onClick={() => setCreatePageOpen(true)}>
              <Plus size={16} weight="bold" />
            </IconButton>
          }
        >
          {loadingTypes ? (
            <div className="text-sm text-neutral-600">Loading...</div>
          ) : pagesFiltered.length === 0 ? (
            <div className="rounded-3xl border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-700">
              <div className="font-semibold text-neutral-900">No pages yet</div>
              <div className="mt-1 text-neutral-600">Create pages to group sections (Home, About, Contact...).</div>
              <div className="mt-3">
                <Btn onClick={() => setCreatePageOpen(true)}>
                  <span className="inline-flex items-center gap-2">
                    <Plus size={18} weight="bold" /> Create Page
                  </span>
                </Btn>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <SearchInput value={pageQ} onChange={setPageQ} placeholder="Search pages..." />

              <button
                type="button"
                onClick={() => setCreatePageOpen(true)}
                className="w-full h-10 rounded-2xl border border-neutral-200 bg-white hover:border-neutral-900 transition text-sm flex items-center justify-between px-3"
              >
                <span className="inline-flex items-center gap-2">
                  <Plus size={16} weight="bold" /> Create Page
                </span>
                <CaretDown size={16} className="text-neutral-400 -rotate-90" />
              </button>

              <div className="space-y-2">
                <AnimatePresence mode="popLayout" initial={false}>
                  {pagesFiltered.map((p) => {
                    const active = p.slug === activePage
                    const count = pageCounts.get(p.slug) || 0
                    return (
                      <motion.button
                        key={p.slug}
                        variants={listItem}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        layout
                        type="button"
                        onClick={() => setActivePage(p.slug)}
                        className={cx(
                          "w-full rounded-3xl border p-3 text-left transition",
                          active
                            ? "border-neutral-900 bg-neutral-900 text-white"
                            : "border-neutral-200 bg-white hover:border-neutral-900"
                        )}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-semibold truncate">{p.name || p.slug}</div>
                            <div className={cx("text-xs truncate", active ? "text-white/70" : "text-neutral-500")}>
                              {p.slug}
                            </div>
                          </div>

                          <span
                            className={cx(
                              "text-xs px-2 py-1 border rounded-full",
                              active ? "border-white/25 text-white/90" : "border-neutral-200 text-neutral-600"
                            )}
                          >
                            {count}
                          </span>
                        </div>
                      </motion.button>
                    )
                  })}
                </AnimatePresence>
              </div>
            </div>
          )}
        </Panel>

        {/* Sections */}
        <Panel
          className="lg:col-span-4"
          title={activePage ? `Sections — ${activePage}` : "Sections"}
          icon={<FileText size={18} />}
          right={
            <div className="flex items-center gap-2">
              <IconButton title="Create Section" tone="dark" onClick={() => setCreateSectionOpen(true)} disabled={!activePage}>
                <Plus size={16} weight="bold" />
              </IconButton>

              {selectedList.length ? (
                <IconButton title="Delete selected" tone="danger" onClick={bulkDelete}>
                  <TrashSimple size={16} />
                </IconButton>
              ) : null}
            </div>
          }
        >
          <div className="space-y-3">
            <SearchInput value={sectionQ} onChange={setSectionQ} placeholder="Search sections..." />

            <div className="rounded-3xl border border-neutral-200 bg-neutral-50 p-3 flex items-center justify-between gap-3">
              <div className="text-xs text-neutral-600">
                {selectedList.length ? (
                  <span>
                    <b>{selectedList.length}</b> selected
                  </span>
                ) : (
                  <span>Select sections for bulk actions</span>
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={selectAllVisible}
                  className="text-xs uppercase tracking-[0.18em] text-neutral-700 hover:text-neutral-900"
                >
                  Select all
                </button>
                <span className="text-neutral-300">•</span>
                <button
                  type="button"
                  onClick={clearSelection}
                  className="text-xs uppercase tracking-[0.18em] text-neutral-700 hover:text-neutral-900"
                >
                  Clear
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setCreateSectionOpen(true)}
              className="w-full h-10 rounded-2xl border border-neutral-200 bg-white hover:border-neutral-900 transition text-sm flex items-center justify-between px-3 disabled:opacity-60"
              disabled={!activePage}
            >
              <span className="inline-flex items-center gap-2">
                <Plus size={16} weight="bold" /> Create Section
              </span>
              <CaretDown size={16} className="text-neutral-400 -rotate-90" />
            </button>

            {loadingTypes ? (
              <div className="text-sm text-neutral-600">Loading...</div>
            ) : sectionsFiltered.length === 0 ? (
              <div className="text-sm text-neutral-600">No sections for this page.</div>
            ) : (
              <div className="space-y-2">
                <AnimatePresence mode="popLayout" initial={false}>
                  {sectionsFiltered.map((s) => {
                    const active = s.slug === activeSectionSlug
                    const selectedOn = selected.has(s.slug)
                    const modeLabel = s.is_single ? "Single" : "Collection"

                    return (
                      <motion.div
                        key={s.slug}
                        variants={listItem}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        layout
                        className={cx(
                          "rounded-3xl border p-3 transition",
                          active ? "border-cyan-300 bg-cyan-50" : "border-neutral-200 bg-white hover:border-neutral-900"
                        )}
                      >
                        <div className="flex items-start gap-3">
                          <div className="pt-1">
                            <Checkbox checked={selectedOn} onChange={() => toggleSelect(s.slug)} />
                          </div>

                          <button
                            type="button"
                            onClick={() => setActiveSectionSlug(s.slug)}
                            className="flex-1 text-left min-w-0"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-neutral-900 truncate">{s.name || s.slug}</div>
                                <div className="text-xs text-neutral-500 truncate">{s.slug}</div>
                              </div>

                              <Badge tone={s.is_single ? "blue" : "neutral"}>{modeLabel}</Badge>
                            </div>

                            <div className="mt-2 text-xs text-neutral-500">
                              {(s.schema?.fields || []).length} fields • {(s.locales || []).join(", ")}
                            </div>
                          </button>
                        </div>

                        <div className="mt-3 flex items-center justify-end gap-2">
                          <IconButton title="Edit schema" onClick={() => openEditSchema(s.id)}>
                            <PencilSimple size={16} />
                          </IconButton>

                          <IconButton
                            title={s.is_single ? "Edit content" : "Open entries"}
                            tone="cyan"
                            onClick={async () => {
                              setActiveSectionSlug(s.slug)
                              // open editor (single -> edit first entry; collection -> create)
                              setTimeout(async () => {
                                try {
                                  const res = await listEntriesBySlug(s.slug)
                                  const list = res?.data?.data || []
                                  const first = list[0]
                                  openEntryEditor(first ? "edit" : "create", first?.id || null)
                                } catch {
                                  openEntryEditor("create", null)
                                }
                              }, 0)
                            }}
                          >
                            <ArrowSquareOut size={16} />
                          </IconButton>

                          <IconButton title="Delete section" tone="danger" onClick={() => onDeleteType(s)}>
                            <TrashSimple size={16} />
                          </IconButton>
                        </div>
                      </motion.div>
                    )
                  })}
                </AnimatePresence>
              </div>
            )}
          </div>
        </Panel>

        {/* Content */}
        <Panel
          className="lg:col-span-5"
          title="Content"
          icon={<FileText size={18} />}
          right={
            activeType ? (
              <div className="flex items-center gap-2">
                <IconButton title="Edit schema" onClick={() => openEditSchema(activeType.id)}>
                  <PencilSimple size={16} />
                </IconButton>

                <IconButton
                  title={activeType.is_single ? "Edit content" : "New entry"}
                  tone="dark"
                  onClick={async () => {
                    if (!activeType) return
                    if (activeType.is_single) {
                      try {
                        const res = await listEntriesBySlug(activeType.slug)
                        const list = res?.data?.data || []
                        const first = list[0]
                        openEntryEditor(first ? "edit" : "create", first?.id || null)
                      } catch {
                        openEntryEditor("create", null)
                      }
                    } else {
                      openEntryEditor("create", null)
                    }
                  }}
                >
                  <Plus size={16} weight="bold" />
                </IconButton>
              </div>
            ) : null
          }
        >
          {!activeType ? (
            <div className="text-sm text-neutral-600">Pick a section to edit its content.</div>
          ) : (
            <div className="space-y-4">
              <ErrorHint text={entriesErr} />

              <div className="rounded-3xl border border-neutral-200 bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-base font-semibold text-neutral-900 truncate">{activeType.name || activeType.slug}</div>
                    <div className="text-xs text-neutral-500 truncate">{activeType.slug}</div>
                  </div>
                  <Badge tone={activeType.is_single ? "blue" : "neutral"}>{activeType.is_single ? "Single" : "Collection"}</Badge>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2">
                  <MiniCard label="Locales" value={(activeType.locales || []).join(", ")} />
                  <MiniCard label="Default" value={activeType.default_locale || "en"} />
                  <MiniCard label="Fields" value={(activeType.schema?.fields || []).length} />
                  <MiniCard label="Entries" value={entries.length} />
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <Btn
                    onClick={async () => {
                      try {
                        const res = await listEntriesBySlug(activeType.slug)
                        const list = res?.data?.data || []
                        if (activeType.is_single) {
                          const first = list[0]
                          openEntryEditor(first ? "edit" : "create", first?.id || null)
                        } else {
                          openEntryEditor("create", null)
                        }
                      } catch {
                        openEntryEditor("create", null)
                      }
                    }}
                  >
                    <span className="inline-flex items-center gap-2">
                      <ArrowSquareOut size={18} />
                      {activeType.is_single ? "Edit Content" : "New Entry"}
                    </span>
                  </Btn>

                  <Btn variant="ghost" onClick={() => openEditSchema(activeType.id)}>
                    <span className="inline-flex items-center gap-2">
                      <PencilSimple size={18} /> Schema
                    </span>
                  </Btn>
                </div>
              </div>

              <div className="rounded-3xl border border-neutral-200 bg-white overflow-hidden">
                <div className="px-4 py-3 border-b border-neutral-100 flex items-center justify-between">
                  <div className="text-sm font-semibold text-neutral-900">{activeType.is_single ? "Single entry" : "Entries"}</div>
                  <div className="text-xs text-neutral-500">{loadingEntries ? "Loading..." : `${entries.length} total`}</div>
                </div>

                {loadingEntries ? (
                  <div className="p-4 text-sm text-neutral-600">Loading entries...</div>
                ) : entries.length === 0 ? (
                  <div className="p-4 text-sm text-neutral-600">
                    No entries yet.
                    <div className="mt-3">
                      <Btn onClick={() => openEntryEditor("create", null)}>
                        <span className="inline-flex items-center gap-2">
                          <Plus size={18} weight="bold" /> Create
                        </span>
                      </Btn>
                    </div>
                  </div>
                ) : (
                  <div className="divide-y divide-neutral-100">
                    <AnimatePresence mode="popLayout" initial={false}>
                      {entries.map((e) => (
                        <motion.div
                          key={e.id}
                          layout
                          variants={listItem}
                          initial="initial"
                          animate="animate"
                          exit="exit"
                          className="px-4 py-3 flex items-center justify-between gap-3"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <Badge tone={e.status === "published" ? "green" : "yellow"}>{e.status}</Badge>
                              <div className="text-xs text-neutral-500 truncate">
                                {e.updated_at ? new Date(e.updated_at).toLocaleString() : "—"}
                              </div>
                            </div>
                            <div className="mt-1 text-xs text-neutral-400">ID: {e.id}</div>
                          </div>

                          <div className="flex items-center gap-2">
                            <IconButton title="Edit" onClick={() => openEntryEditor("edit", e.id)}>
                              <PencilSimple size={16} />
                            </IconButton>

                            <IconButton
                              title={e.status === "published" ? "Unpublish" : "Publish"}
                              tone="cyan"
                              onClick={() => togglePublishEntry(e)}
                            >
                              <ArrowSquareOut size={16} />
                            </IconButton>

                            {!activeType.is_single ? (
                              <IconButton title="Delete" tone="danger" onClick={() => onDeleteEntry(e)}>
                                <TrashSimple size={16} />
                              </IconButton>
                            ) : null}
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                )}
              </div>

              <div className="rounded-3xl border border-neutral-200 bg-neutral-50 p-4">
                <div className="text-[11px] uppercase tracking-[0.22em] text-neutral-500">Slug convention</div>
                <div className="mt-2 text-sm text-neutral-700 leading-6">
                  Group sections under a page:
                  <div className="mt-2 font-mono text-xs rounded-2xl border border-neutral-200 bg-white p-2">
                    {activePage || "page"}-section
                  </div>
                </div>
              </div>
            </div>
          )}
        </Panel>
      </div>

      {/* Modals */}
      <CreatePageModal open={createPageOpen} onClose={() => setCreatePageOpen(false)} onCreate={onCreatePage} />

      <CreateSectionModal
        open={createSectionOpen}
        onClose={() => setCreateSectionOpen(false)}
        pageSlug={activePage}
        onCreate={onCreateSection}
      />

      <SchemaModal
        open={schemaOpen}
        onClose={() => {
          setSchemaOpen(false)
          schemaPreloadRef.current = null
        }}
        mode={schemaMode}
        id={schemaId}
        preload={schemaPreloadRef.current}
        onSaved={async (savedSlug) => {
          await loadTypes()
          if (savedSlug) setActiveSectionSlug(savedSlug)
          setSchemaOpen(false)
          schemaPreloadRef.current = null
        }}
      />

      <EntryEditorModal
        open={entryOpen}
        onClose={() => setEntryOpen(false)}
        type={activeType}
        mode={entryMode}
        entryId={entryId}
        onSaved={async () => {
          await refreshEntries(activeType?.slug)
          setEntryOpen(false)
        }}
        mediaOpen={mediaOpen}
        setMediaOpen={setMediaOpen}
        mediaCtx={mediaCtx}
        setMediaCtx={setMediaCtx}
      />

      <ConfirmModal
        open={confirm.open}
        title={confirm.title}
        description={confirm.description}
        danger={confirm.danger}
        onCancel={() => setConfirm((p) => ({ ...p, open: false, action: null }))}
        onConfirm={() => confirm.action && confirm.action()}
      />
    </motion.div>
  )
}

/* ------------------------------ Create Page Modal ------------------------------ */

function CreatePageModal({ open, onClose, onCreate }) {
  const [name, setName] = useState("")
  const [slug, setSlug] = useState("")

  useEffect(() => {
    if (!open) return
    setName("")
    setSlug("")
  }, [open])

  return (
    <Modal open={open} onClose={onClose} title="Create Page" icon={<FolderSimple size={18} />} max="max-w-lg">
      <div className="space-y-4">
        <div>
          <Label>Name</Label>
          <Input
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              if (!slug) setSlug(normalizeSlug(e.target.value))
            }}
            placeholder="Home"
          />
        </div>

        <div>
          <Label>Slug</Label>
          <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="home" />
          <div className="mt-2 text-xs text-neutral-500">Pages are folders to organize sections.</div>
        </div>

        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn
            onClick={() => {
              onCreate({ name: name.trim(), slug: slug.trim() })
              onClose()
            }}
            disabled={!name.trim() && !slug.trim()}
          >
            <span className="inline-flex items-center gap-2">
              <Plus size={18} weight="bold" /> Create
            </span>
          </Btn>
        </div>
      </div>
    </Modal>
  )
}

/* ------------------------------ Create Section Modal ------------------------------ */

function CreateSectionModal({ open, onClose, pageSlug, onCreate }) {
  const [sectionName, setSectionName] = useState("")
  const [sectionSlug, setSectionSlug] = useState("")

  useEffect(() => {
    if (!open) return
    setSectionName("")
    setSectionSlug("")
  }, [open])

  return (
    <Modal open={open} onClose={onClose} title="Create Section" icon={<FileText size={18} />} max="max-w-lg">
      <div className="space-y-4">
        <div className="rounded-3xl border border-neutral-200 bg-neutral-50 p-3 text-sm">
          <span className="text-neutral-600">Page: </span>
          <span className="font-semibold text-neutral-900">{pageSlug || "—"}</span>
        </div>

        <div>
          <Label>Section name</Label>
          <Input
            value={sectionName}
            onChange={(e) => {
              setSectionName(e.target.value)
              if (!sectionSlug) setSectionSlug(normalizeSlug(e.target.value))
            }}
            placeholder="About"
          />
        </div>

        <div>
          <Label>Section slug</Label>
          <Input value={sectionSlug} onChange={(e) => setSectionSlug(e.target.value)} placeholder="about" />
          <div className="mt-2 text-xs text-neutral-500">
            Full slug:
            <div className="mt-2 font-mono text-xs rounded-2xl border border-neutral-200 bg-white p-2">
              {(pageSlug || "page") + "-" + (sectionSlug || "section")}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn
            onClick={() => {
              onCreate({ sectionName: sectionName.trim(), sectionSlug: sectionSlug.trim() })
              onClose()
            }}
            disabled={!pageSlug || (!sectionName.trim() && !sectionSlug.trim())}
          >
            <span className="inline-flex items-center gap-2">
              <Plus size={18} weight="bold" /> Next
            </span>
          </Btn>
        </div>
      </div>
    </Modal>
  )
}

/* ------------------------------ Schema Modal ------------------------------ */

function SchemaModal({ open, onClose, mode, id, preload, onSaved }) {
  const [loading, setLoading] = useState(mode === "edit")
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState("")

  const [form, setForm] = useState({
    name: "",
    slug: "",
    is_single: true,
    locales: ["en", "fr", "ar"],
    default_locale: "en",
    schema: { fields: [emptyField()] },
  })

  useEffect(() => {
    if (!open) return
    setErr("")
    setLoading(mode === "edit")
    setSaving(false)

    if (mode !== "edit") {
      setForm((p) => ({
        ...p,
        name: preload?.preName || "",
        slug: preload?.preSlug || "",
        schema: { fields: [emptyField()] },
      }))
    }
  }, [open, mode, preload])

  useEffect(() => {
    if (!open) return
    if (mode !== "edit" || !id) return

      ; (async () => {
        setLoading(true)
        setErr("")
        try {
          const res = await getContentType(id)
          const t = res?.data?.data
          setForm({
            name: t?.name || "",
            slug: t?.slug || "",
            is_single: !!t?.is_single,
            locales: Array.isArray(t?.locales) && t.locales.length ? t.locales : ["en"],
            default_locale: t?.default_locale || "en",
            schema: { fields: t?.schema?.fields?.length ? t.schema.fields : [emptyField()] },
          })
        } catch (e) {
          setErr(e?.message || "Failed to load schema.")
        } finally {
          setLoading(false)
        }
      })()
  }, [open, mode, id])

  const fields = useMemo(() => (Array.isArray(form.schema?.fields) ? form.schema.fields : []), [form.schema])

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }))
  const setSchemaFields = (next) => setForm((p) => ({ ...p, schema: { fields: next } }))

  const addField = () => setSchemaFields([...(fields || []), emptyField()])
  const removeField = (idx) => setSchemaFields((fields || []).filter((_, i) => i !== idx))
  const updateField = (idx, patch) => setSchemaFields((fields || []).map((f, i) => (i === idx ? { ...f, ...patch } : f)))

  const addRepeaterSubField = (idx) => {
    const f = fields[idx]
    const subs = Array.isArray(f?.fields) ? f.fields : []
    updateField(idx, { fields: [...subs, emptyField()] })
  }
  const updateRepeaterSubField = (idx, sIdx, patch) => {
    const f = fields[idx]
    const subs = Array.isArray(f?.fields) ? f.fields : []
    updateField(idx, { fields: subs.map((sf, j) => (j === sIdx ? { ...sf, ...patch } : sf)) })
  }
  const removeRepeaterSubField = (idx, sIdx) => {
    const f = fields[idx]
    const subs = Array.isArray(f?.fields) ? f.fields : []
    updateField(idx, { fields: subs.filter((_, j) => j !== sIdx) })
  }

  const onSave = async () => {
    const payload = {
      name: (form.name || "").trim(),
      slug: (form.slug || "").trim(),
      is_single: !!form.is_single,
      locales: Array.isArray(form.locales) && form.locales.length ? form.locales : ["en"],
      default_locale: (form.default_locale || "en").trim(),
      schema: { fields: (fields || []).filter((f) => f && String(f.key || "").trim()) },
    }

    if (!payload.name || !payload.slug) return

    setSaving(true)
    setErr("")
    try {
      if (mode === "edit" && id) await updateContentType(id, payload)
      else await createContentType(payload)
      onSaved?.(payload.slug)
    } catch (e) {
      setErr(e?.message || "Failed to save schema.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === "edit" ? "Edit Section Schema" : "Create Section Schema"}
      icon={<FileText size={18} />}
      max="max-w-6xl"
    >
      {loading ? (
        <div className="text-sm text-neutral-600">Loading...</div>
      ) : (
        <div className="space-y-4 max-h-[80vh] overflow-y-auto">
          <ErrorHint text={err} />

          {/* top settings */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
            <div className="lg:col-span-4 rounded-3xl border border-neutral-200 p-3">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Homepage Hero" />
            </div>

            <div className="lg:col-span-4 rounded-3xl border border-neutral-200 p-3">
              <Label>Slug</Label>
              <Input value={form.slug} onChange={(e) => setField("slug", e.target.value)} placeholder="home-hero" />
            </div>

            <div className="lg:col-span-2 rounded-3xl border border-neutral-200 p-3">
              <Label>Mode</Label>
              <PortalSelect value={form.is_single ? "single" : "collection"} onChange={(e) => setField("is_single", e.target.value === "single")}>
                <option value="single">Single</option>
                <option value="collection">Collection</option>
              </PortalSelect>
            </div>

            <div className="lg:col-span-2 rounded-3xl border border-neutral-200 p-3">
              <Label>Default</Label>
              <Input value={form.default_locale} onChange={(e) => setField("default_locale", e.target.value.trim())} placeholder="en" />
            </div>

            <div className="lg:col-span-12 rounded-3xl border border-neutral-200 p-3">
              <Label>Locales (comma)</Label>
              <Input
                value={(form.locales || []).join(",")}
                onChange={(e) =>
                  setField(
                    "locales",
                    e.target.value
                      .split(",")
                      .map((x) => x.trim())
                      .filter(Boolean)
                  )
                }
                placeholder="en,fr,ar"
              />
            </div>
          </div>

          {/* fields builder */}
          <div className="rounded-[28px] border border-neutral-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-neutral-100 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-neutral-900">Fields</div>
                <div className="text-xs text-neutral-500">Build your section schema.</div>
              </div>
              <Btn variant="ghost" onClick={addField}>
                <span className="inline-flex items-center gap-2">
                  <Plus size={18} weight="bold" /> Add
                </span>
              </Btn>
            </div>

            <div className="p-4 space-y-3">
              <AnimatePresence mode="popLayout" initial={false}>
                {fields.map((f, idx) => (
                  <motion.div
                    key={`${f.key || "field"}-${idx}`}
                    layout
                    variants={listItem}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    className="rounded-3xl border border-neutral-200 p-4 bg-white"
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold text-neutral-900">Field #{idx + 1}</div>
                      <IconButton title="Remove field" tone="danger" onClick={() => removeField(idx)}>
                        <TrashSimple size={16} />
                      </IconButton>
                    </div>

                    <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-3">
                      <div className="lg:col-span-3">
                        <Label>Key</Label>
                        <Input value={f.key} onChange={(e) => updateField(idx, { key: e.target.value })} placeholder="title" />
                      </div>

                      <div className="lg:col-span-3">
                        <Label>Label</Label>
                        <Input value={f.label || ""} onChange={(e) => updateField(idx, { label: e.target.value })} placeholder="Title" />
                      </div>

                      <div className="lg:col-span-3">
                        <Label>Type</Label>
                        <PortalSelect value={f.type} onChange={(e) => updateField(idx, { type: e.target.value })}>
                          {FIELD_TYPES.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </PortalSelect>
                      </div>

                      <div className="lg:col-span-3 flex flex-wrap items-end gap-2">
                        <Switch checked={!!f.required} onChange={(v) => updateField(idx, { required: v })} label="Required" />
                        <Switch
                          checked={!!f.localized}
                          onChange={(v) => updateField(idx, { localized: v })}
                          label="Localized"
                          disabled={f.type === "repeater"}
                        />
                        {f.type === "media" ? (
                          <Switch checked={!!f.multiple} onChange={(v) => updateField(idx, { multiple: v })} label="Multiple" />
                        ) : null}
                      </div>
                    </div>

                    {f.type === "enum" ? (
                      <div className="mt-4">
                        <Label>Enum values (comma)</Label>
                        <Input
                          value={(f.values || []).join(",")}
                          onChange={(e) =>
                            updateField(idx, {
                              values: e.target.value
                                .split(",")
                                .map((x) => x.trim())
                                .filter(Boolean),
                            })
                          }
                          placeholder="draft,published"
                        />
                      </div>
                    ) : null}

                    {f.type === "number" ? (
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label>Min</Label>
                          <Input
                            type="number"
                            value={f.min ?? ""}
                            onChange={(e) => updateField(idx, { min: e.target.value === "" ? undefined : Number(e.target.value) })}
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <Label>Max</Label>
                          <Input
                            type="number"
                            value={f.max ?? ""}
                            onChange={(e) => updateField(idx, { max: e.target.value === "" ? undefined : Number(e.target.value) })}
                            placeholder="100"
                          />
                        </div>
                      </div>
                    ) : null}

                    {["string", "text", "richtext"].includes(f.type) ? (
                      <div className="mt-4">
                        <Label>Regex (optional)</Label>
                        <Input value={f.regex || ""} onChange={(e) => updateField(idx, { regex: e.target.value })} placeholder="^.{0,120}$" />
                      </div>
                    ) : null}

                    {f.type === "link" ? (
                      <div className="mt-4 rounded-3xl border border-neutral-200 bg-neutral-50 p-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-sm font-semibold text-neutral-900">Link field</div>
                          <Switch
                            checked={f.link_with_image !== false}
                            onChange={(v) => updateField(idx, { link_with_image: v })}
                            label="With image"
                          />
                        </div>
                        <div className="mt-2 text-xs text-neutral-500">Stores: label + url {f.link_with_image !== false ? "+ image" : ""}</div>
                      </div>
                    ) : null}

                    {f.type === "repeater" ? (
                      <div className="mt-4 rounded-3xl border border-neutral-200 bg-neutral-50 p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-semibold text-neutral-900">Repeater fields</div>
                            <div className="text-xs text-neutral-500">Sub-fields for each item.</div>
                          </div>
                          <Btn variant="ghost" className="h-9 px-3" onClick={() => addRepeaterSubField(idx)}>
                            Add sub field
                          </Btn>
                        </div>

                        <div className="mt-3 space-y-2">
                          <AnimatePresence mode="popLayout" initial={false}>
                            {(f.fields || []).map((sf, sIdx) => (
                              <motion.div
                                key={`${sf.key || "sub"}-${sIdx}`}
                                layout
                                variants={listItem}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                className="rounded-3xl border border-neutral-200 bg-white p-3"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="text-xs font-semibold text-neutral-700">Item field #{sIdx + 1}</div>
                                  <IconButton title="Remove sub field" tone="danger" onClick={() => removeRepeaterSubField(idx, sIdx)}>
                                    <TrashSimple size={16} />
                                  </IconButton>
                                </div>

                                <div className="mt-3 grid grid-cols-1 md:grid-cols-12 gap-2">
                                  <div className="md:col-span-4">
                                    <Label>Key</Label>
                                    <Input value={sf.key} onChange={(e) => updateRepeaterSubField(idx, sIdx, { key: e.target.value })} placeholder="label" />
                                  </div>

                                  <div className="md:col-span-4">
                                    <Label>Type</Label>
                                    <PortalSelect value={sf.type} onChange={(e) => updateRepeaterSubField(idx, sIdx, { type: e.target.value })}>
                                      {FIELD_TYPES.filter((t) => t !== "repeater").map((t) => (
                                        <option key={t} value={t}>
                                          {t}
                                        </option>
                                      ))}
                                    </PortalSelect>
                                  </div>

                                  <div className="md:col-span-4 flex flex-wrap items-end gap-2">
                                    <Switch checked={!!sf.required} onChange={(v) => updateRepeaterSubField(idx, sIdx, { required: v })} label="Required" />
                                    <Switch checked={!!sf.localized} onChange={(v) => updateRepeaterSubField(idx, sIdx, { localized: v })} label="Localized" />
                                    {sf.type === "media" ? (
                                      <Switch checked={!!sf.multiple} onChange={(v) => updateRepeaterSubField(idx, sIdx, { multiple: v })} label="Multiple" />
                                    ) : null}
                                  </div>
                                </div>

                                {sf.type === "enum" ? (
                                  <div className="mt-3">
                                    <Label>Enum values (comma)</Label>
                                    <Input
                                      value={(sf.values || []).join(",")}
                                      onChange={(e) =>
                                        updateRepeaterSubField(idx, sIdx, {
                                          values: e.target.value
                                            .split(",")
                                            .map((x) => x.trim())
                                            .filter(Boolean),
                                        })
                                      }
                                      placeholder="a,b,c"
                                    />
                                  </div>
                                ) : null}
                              </motion.div>
                            ))}
                          </AnimatePresence>

                          {(f.fields || []).length === 0 ? <div className="text-sm text-neutral-600">No repeater fields yet.</div> : null}
                        </div>
                      </div>
                    ) : null}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Btn variant="ghost" onClick={onClose}>
              Cancel
            </Btn>
            <Btn onClick={onSave} disabled={saving || !form.name.trim() || !form.slug.trim()}>
              {saving ? "Saving..." : "Save"}
            </Btn>
          </div>
        </div>
      )}
    </Modal>
  )
}

/* ------------------------------ Entry Editor ------------------------------ */
/* ✅ REPLACE your whole "EntryEditorModal + Field Renderers" block with this.
   Fixes:
   - ✅ ROOT media field now renders Media Picker + preview (single/multiple)
   - ✅ Localized MEDIA / ENUM / JSON supported
   - ✅ Repeater subfields support: string, enum, json, media, link, text, number, boolean, date
   - ✅ Media picking works for: root + repeater + localized + multiple
*/

/* ------------------------------ Entry Editor ------------------------------ */

function EntryEditorModal({
  open,
  onClose,
  type,
  mode,
  entryId,
  onSaved,

  mediaOpen,
  setMediaOpen,
  mediaCtx,
  setMediaCtx,
}) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState("")

  const [activeLocale, setActiveLocale] = useState("en")
  const [data, setData] = useState({})
  const [localEntries, setLocalEntries] = useState([])

  const fields = Array.isArray(type?.schema?.fields) ? type.schema.fields : []
  const locales = Array.isArray(type?.locales) && type.locales.length ? type.locales : ["en"]
  const defaultLocale = type?.default_locale || "en"

  useEffect(() => {
    if (!open) return
    if (!type?.slug) return

      ; (async () => {
        setLoading(true)
        setSaving(false)
        setErr("")

        const loc = type?.default_locale || "en"
        setActiveLocale(loc)

        try {
          const res = await listEntriesBySlug(type.slug)
          const list = res?.data?.data || []
          setLocalEntries(list)

          if (mode === "edit") {
            const found = list.find((x) => String(x.id) === String(entryId))
            setData(found?.data || {})
          } else {
            setData({})
          }
        } catch (e) {
          setErr(e?.message || "Failed to load entry.")
          setLocalEntries([])
          setData({})
        } finally {
          setLoading(false)
        }
      })()
  }, [open, type?.slug, type?.default_locale, mode, entryId])

  const setValue = useCallback((key, value) => setData((p) => ({ ...p, [key]: value })), [])

  const setLocalizedValue = useCallback(
    (key, locale, value) => {
      setData((p) => {
        const prev = ensureLocalized(p[key], locales, "")
        return { ...p, [key]: { ...prev, [locale]: value } }
      })
    },
    [locales]
  )

  const setRepeaterItem = useCallback((key, idx, patch) => {
    setData((p) => {
      const arr = Array.isArray(p[key]) ? [...p[key]] : []
      arr[idx] = { ...(arr[idx] || {}), ...patch }
      return { ...p, [key]: arr }
    })
  }, [])

  const addRepeaterItem = useCallback((key) => {
    setData((p) => {
      const arr = Array.isArray(p[key]) ? [...p[key]] : []
      arr.push({})
      return { ...p, [key]: arr }
    })
  }, [])

  const removeRepeaterItem = useCallback((key, idx) => {
    setData((p) => {
      const arr = Array.isArray(p[key]) ? [...p[key]] : []
      arr.splice(idx, 1)
      return { ...p, [key]: arr }
    })
  }, [])

  const openMedia = useCallback(
    (ctx) => {
      setMediaCtx(ctx)
      setMediaOpen(true)
    },
    [setMediaCtx, setMediaOpen]
  )

  /* ✅ FIXED: supports media picking for root/repeater + localized + multiple */
  const onPickMedia = useCallback(
    (picked) => {
      if (!mediaCtx) return
      const assets = Array.isArray(picked) ? picked : [picked]
      const urls = assets.map((a) => a?.url).filter(Boolean)
      if (!urls.length) return

      const kind = mediaCtx.kind || "media"

      // ✅ LINK IMAGE (root or repeater)
      const applyLinkImage = ({ key, locale, localized, url }) => {
        setData((p) => {
          if (!localized) {
            const prev = isObj(p[key]) ? p[key] : emptyLinkValue()
            return { ...p, [key]: { ...prev, image: url } }
          }
          const prev = ensureLocalizedObject(p[key], locales, emptyLinkValue())
          const cur = isObj(prev[locale]) ? prev[locale] : emptyLinkValue()
          return { ...p, [key]: { ...prev, [locale]: { ...cur, image: url } } }
        })
      }

      // ✅ ROOT PICK
      if (mediaCtx.mode === "root") {
        if (kind === "link-image") {
          applyLinkImage({
            key: mediaCtx.key,
            locale: mediaCtx.locale,
            localized: !!mediaCtx.localized,
            url: urls[0],
          })
          return
        }

        // ✅ ROOT MEDIA (supports localized)
        const { key, multiple, localized, locale } = mediaCtx

        if (localized) {
          setData((p) => {
            const fallback = multiple ? [] : ""
            const prev = ensureLocalized(p[key], locales, fallback)
            const cur = prev?.[locale] ?? fallback

            const nextVal = multiple
              ? [...(Array.isArray(cur) ? cur : []), ...urls]
              : urls[0]

            return { ...p, [key]: { ...prev, [locale]: nextVal } }
          })
          return
        }

        // non-localized
        if (multiple) {
          setData((p) => {
            const cur = Array.isArray(p[key]) ? p[key] : []
            return { ...p, [key]: [...cur, ...urls] }
          })
        } else {
          setValue(key, urls[0])
        }
        return
      }

      // ✅ REPEATER PICK
      if (mediaCtx.mode === "repeater") {
        const {
          parentKey,
          idx,
          subKey,
          multiple,
          localized,
          locale,
          kind: subKind,
        } = mediaCtx

        // repeater link-image
        if ((subKind || kind) === "link-image") {
          setData((p) => {
            const arr = Array.isArray(p[parentKey]) ? [...p[parentKey]] : []
            const item = { ...(arr[idx] || {}) }

            if (!localized) {
              const prev = isObj(item[subKey]) ? item[subKey] : emptyLinkValue()
              item[subKey] = { ...prev, image: urls[0] }
            } else {
              const prev = ensureLocalizedObject(item[subKey], locales, emptyLinkValue())
              const cur = isObj(prev[locale]) ? prev[locale] : emptyLinkValue()
              item[subKey] = { ...prev, [locale]: { ...cur, image: urls[0] } }
            }

            arr[idx] = item
            return { ...p, [parentKey]: arr }
          })
          return
        }

        // repeater media (supports localized)
        setData((p) => {
          const arr = Array.isArray(p[parentKey]) ? [...p[parentKey]] : []
          const item = { ...(arr[idx] || {}) }

          if (localized) {
            const fallback = multiple ? [] : ""
            const prev = ensureLocalized(item[subKey], locales, fallback)
            const cur = prev?.[locale] ?? fallback
            const nextVal = multiple
              ? [...(Array.isArray(cur) ? cur : []), ...urls]
              : urls[0]
            item[subKey] = { ...prev, [locale]: nextVal }
          } else {
            if (multiple) {
              const cur = Array.isArray(item[subKey]) ? item[subKey] : []
              item[subKey] = [...cur, ...urls]
            } else {
              item[subKey] = urls[0]
            }
          }

          arr[idx] = item
          return { ...p, [parentKey]: arr }
        })
      }
    },
    [mediaCtx, locales, setValue]
  )

  const onSave = async () => {
    if (!type?.slug) return
    setSaving(true)
    setErr("")
    try {
      const payload = { data }
      if (mode === "edit" && entryId) await updateEntry(entryId, payload)
      else await upsertEntryBySlug(type.slug, payload)
      onSaved?.()
    } catch (e) {
      setErr(e?.message || "Failed to save entry.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={type ? `${type.name || type.slug} — ${mode === "edit" ? "Edit" : "New"}` : "Editor"}
        icon={<FileText size={18} />}
        max="max-w-6xl"
      >
        {!type ? (
          <div className="text-sm text-neutral-600">Pick a section first.</div>
        ) : loading ? (
          <div className="text-sm text-neutral-600">Loading...</div>
        ) : (
          <div className="space-y-4 max-h-[80vh] overflow-y-auto">
            <ErrorHint text={err} />

            {/* locale tabs */}
            <div className="flex flex-wrap gap-2">
              {locales.map((l) => (
                <motion.button
                  key={l}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setActiveLocale(l)}
                  className={cx(
                    "h-10 px-4 rounded-2xl text-sm border transition",
                    activeLocale === l
                      ? "bg-neutral-900 text-white border-neutral-900"
                      : "bg-white border-neutral-200 hover:border-neutral-900"
                  )}
                >
                  {l.toUpperCase()}
                  {l === defaultLocale ? (
                    <span className="ml-2 text-xs opacity-80">(default)</span>
                  ) : null}
                </motion.button>
              ))}
            </div>

            {/* form */}
            <div className="rounded-[28px] border border-neutral-200 bg-white p-4 space-y-5">
              {fields.map((f) => (
                <FieldRenderer
                  key={f.key}
                  field={f}
                  value={data[f.key]}
                  locales={locales}
                  activeLocale={activeLocale}
                  setValue={setValue}
                  setLocalizedValue={setLocalizedValue}
                  setRepeaterItem={setRepeaterItem}
                  addRepeaterItem={addRepeaterItem}
                  removeRepeaterItem={removeRepeaterItem}
                  openMedia={openMedia}
                />
              ))}
              {!fields.length ? (
                <div className="text-sm text-neutral-600">This section has no fields yet.</div>
              ) : null}
            </div>

            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={onClose}>
                Cancel
              </Btn>
              <Btn onClick={onSave} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Btn>
            </div>
          </div>
        )}
      </Modal>

      <MediaLibraryModal
        open={mediaOpen}
        onClose={() => setMediaOpen(false)}
        onPick={onPickMedia}
        folder="content"
        multiple={!!mediaCtx?.multiple}
      />
    </>
  )
}

/* ------------------------------ Field Renderers ------------------------------ */

function MediaPreview({ urls, onRemove }) {
  const list = Array.isArray(urls) ? urls : urls ? [urls] : []
  if (!list.length) return null

  return (
    <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
      {list.map((u, idx) => (
        <div
          key={`${u}-${idx}`}
          className="relative rounded-2xl border border-neutral-200 overflow-hidden bg-neutral-50"
        >
          <img src={u} alt="" className="w-full h-24 object-cover" />
          <button
            type="button"
            onClick={() => onRemove(idx)}
            className="absolute top-2 right-2 h-8 w-8 rounded-xl bg-white/90 border border-neutral-200 grid place-items-center hover:border-neutral-900 transition"
            title="Remove"
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  )
}

function MediaFieldEditor({
  label,
  value,
  multiple,
  onPick,
  onClear,
  onRemoveIndex,
}) {
  const urls = multiple ? (Array.isArray(value) ? value : []) : value ? String(value) : ""

  return (
    <div className="w-full">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <div className="flex items-center gap-2">
          <Btn variant="ghost" className="h-9 px-3" onClick={onPick}>
            <span className="inline-flex items-center gap-2">
              <ImageIcon size={18} /> Pick
            </span>
          </Btn>

          {multiple ? (
            Array.isArray(urls) && urls.length ? (
              <Btn variant="ghost" className="h-9 px-3" onClick={onClear}>
                Clear
              </Btn>
            ) : null
          ) : urls ? (
            <Btn variant="ghost" className="h-9 px-3" onClick={onClear}>
              Clear
            </Btn>
          ) : null}
        </div>
      </div>

      {multiple ? (
        urls.length ? (
          <MediaPreview urls={urls} onRemove={onRemoveIndex} />
        ) : (
          <div className="mt-2 text-xs text-neutral-500">No media selected.</div>
        )
      ) : urls ? (
        <MediaPreview urls={[urls]} onRemove={() => onClear()} />
      ) : (
        <div className="mt-2 text-xs text-neutral-500">No media selected.</div>
      )}
    </div>
  )
}

function LinkEditor({ label, value, onChange, withImage = true, onPickImage, onClearImage }) {
  const v = isObj(value) ? value : emptyLinkValue()
  return (
    <div className="mt-2 rounded-3xl border border-neutral-200 bg-white p-3">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
        <div className="md:col-span-5">
          <Label>{label} label</Label>
          <Input
            value={v.label || ""}
            onChange={(e) => onChange({ ...v, label: e.target.value })}
            placeholder="Button text"
          />
        </div>
        <div className="md:col-span-7">
          <Label>{label} URL</Label>
          <Input
            value={v.url || ""}
            onChange={(e) => onChange({ ...v, url: e.target.value })}
            placeholder="https://..."
          />
        </div>
      </div>

      {withImage ? (
        <div className="mt-3 rounded-3xl border border-neutral-200 bg-neutral-50 p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-medium text-neutral-900">
              <ImageIcon size={18} /> Image (optional)
            </div>
            <div className="flex items-center gap-2">
              <Btn variant="ghost" className="h-9 px-3" onClick={onPickImage}>
                Pick image
              </Btn>
              {v.image ? (
                <Btn variant="ghost" className="h-9 px-3" onClick={onClearImage}>
                  Clear
                </Btn>
              ) : null}
            </div>
          </div>

          {v.image ? (
            <div className="mt-2 rounded-2xl border border-neutral-200 overflow-hidden bg-white">
              <img src={v.image} alt="" className="w-full h-40 object-cover" />
            </div>
          ) : (
            <div className="mt-2 text-xs text-neutral-500">No image selected.</div>
          )}
        </div>
      ) : null}
    </div>
  )
}

function JsonField({ label, value, onValue }) {
  const normalize = (v) => {
    if (Array.isArray(v)) return v
    if (isObj(v)) return Object.entries(v).map(([k, val]) => ({ key: k, value: val }))
    return []
  }

  const [rows, setRows] = useState(() => normalize(value))

  useEffect(() => {
    setRows(normalize(value))
  }, [value])

  const update = (next) => {
    setRows(next)
    onValue(next) // ✅ keep as array (backend-ready)
  }

  return (
    <div className="space-y-2 mt-2 w-full">
      {rows.map((row, idx) => (
        <div key={idx} className="flex justify-center w-full gap-2 items-center">
          <Input
            placeholder="Key"
            value={row.key || ""}
            onChange={(e) => {
              const next = [...rows]
              next[idx] = { ...next[idx], key: e.target.value }
              update(next)
            }}
          />
          <Input
            placeholder="Value"
            value={row.value ?? ""}
            onChange={(e) => {
              const next = [...rows]
              next[idx] = { ...next[idx], value: e.target.value }
              update(next)
            }}
          />
          <button
            type="button"
            onClick={() => {
              const next = rows.filter((_, i) => i !== idx)
              update(next)
            }}
            className="flex justify-center mt-2 items-center h-10 w-24 rounded-xl border border-neutral-200 hover:border-neutral-900"
          >
            <X size={14} />
          </button>
        </div>
      ))}

      <Btn variant="ghost" className="h-9 px-3" onClick={() => update([...rows, { key: "", value: "" }])}>
        + Add
      </Btn>
    </div>
  )
}

/* ✅ small helpers for correct defaults */
function fallbackForField(field) {
  const t = field?.type
  if (t === "json") return []
  if (t === "media") return field?.multiple ? [] : ""
  if (t === "boolean") return false
  if (t === "number") return ""
  return ""
}

/* ✅ MAIN FIELD (root) */
function FieldRenderer({
  field,
  value,
  locales,
  activeLocale,
  setValue,
  setLocalizedValue,
  setRepeaterItem,
  addRepeaterItem,
  removeRepeaterItem,
  openMedia,
}) {
  const key = field.key
  const label = field.label || key
  const type = field.type

  /* -------------------------------- REPEATER -------------------------------- */
  if (type === "repeater") {
    const arr = Array.isArray(value) ? value : []
    const subFields = Array.isArray(field.fields) ? field.fields : []

    return (
      <div>
        <div className="flex items-center justify-between">
          <div className="text-xs text-neutral-500">{label}</div>
          <Btn variant="ghost" onClick={() => addRepeaterItem(key)}>
            Add item
          </Btn>
        </div>

        <div className="mt-3 space-y-3">
          {arr.map((item, idx) => (
            <div key={idx} className="rounded-3xl border border-neutral-200 p-4 bg-neutral-50">
              <div className="flex justify-between">
                <div className="text-sm font-semibold">Item #{idx + 1}</div>
                <Btn variant="danger" onClick={() => removeRepeaterItem(key, idx)}>
                  Remove
                </Btn>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-12 gap-3">
                {subFields.map((sf) => (
                  <RepeaterSubField
                    key={sf.key}
                    parentKey={key}
                    idx={idx}
                    field={sf}
                    value={item?.[sf.key]}
                    locales={locales}
                    activeLocale={activeLocale}
                    setRepeaterItem={setRepeaterItem}
                    openMedia={openMedia}
                  />
                ))}
              </div>
            </div>
          ))}

          {!arr.length ? <div className="text-sm text-neutral-600">No items yet.</div> : null}
        </div>
      </div>
    )
  }

  /* ------------------------- LOCALIZED (NON-REPEATER) ------------------------- */
  if (field.localized) {
    // localized LINK
    if (type === "link") {
      const v = ensureLocalizedObject(value, locales, emptyLinkValue())
      const cur = v[activeLocale] || emptyLinkValue()

      return (
        <div className="w-full">
          <Label>
            {label} ({activeLocale.toUpperCase()})
          </Label>

          <LinkEditor
            label={label}
            value={cur}
            withImage={field.link_with_image !== false}
            onChange={(next) => setValue(key, { ...v, [activeLocale]: next })}
            onPickImage={() =>
              openMedia({
                mode: "root",
                kind: "link-image",
                key,
                localized: true,
                locale: activeLocale,
                multiple: false,
              })
            }
            onClearImage={() =>
              setValue(key, {
                ...v,
                [activeLocale]: { ...(v[activeLocale] || emptyLinkValue()), image: "" },
              })
            }
          />
        </div>
      )
    }

    // localized MEDIA
    if (type === "media") {
      const multiple = !!field.multiple
      const v = ensureLocalized(value, locales, multiple ? [] : "")
      const cur = v?.[activeLocale] ?? (multiple ? [] : "")

      return (
        <div className="w-full">
          <MediaFieldEditor
            label={`${label} (${activeLocale.toUpperCase()})`}
            value={cur}
            multiple={multiple}
            onPick={() =>
              openMedia({
                mode: "root",
                kind: "media",
                key,
                multiple,
                localized: true,
                locale: activeLocale,
              })
            }
            onClear={() => setValue(key, { ...v, [activeLocale]: multiple ? [] : "" })}
            onRemoveIndex={(rIdx) => {
              if (!multiple) return
              const arr = Array.isArray(cur) ? [...cur] : []
              arr.splice(rIdx, 1)
              setValue(key, { ...v, [activeLocale]: arr })
            }}
          />
        </div>
      )
    }

    // localized JSON
    if (type === "json") {
      const v = ensureLocalized(value, locales, [])
      const cur = v?.[activeLocale] ?? []

      return (
        <div className="w-full">
          <Label>
            {label} ({activeLocale.toUpperCase()})
          </Label>
          <JsonField value={cur} onValue={(val) => setValue(key, { ...v, [activeLocale]: val })} />
        </div>
      )
    }

    // localized ENUM
    if (type === "enum") {
      const v = ensureLocalized(value, locales, "")
      const cur = v?.[activeLocale] ?? ""
      const options = Array.isArray(field.values) ? field.values : []

      return (
        <div className="w-full">
          <Label>
            {label} ({activeLocale.toUpperCase()})
          </Label>
          <PortalSelect value={cur} onChange={(e) => setValue(key, { ...v, [activeLocale]: e.target.value })}>
            <option value="">Select...</option>
            {options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </PortalSelect>
        </div>
      )
    }

    // localized SIMPLE TYPES
    const v = ensureLocalized(value, locales, fallbackForField(field))
    const cur = v?.[activeLocale]

    if (type === "text") {
      return (
        <div className="w-full">
          <Label>
            {label} ({activeLocale.toUpperCase()})
          </Label>
          <Textarea
            rows={5}
            value={cur ?? ""}
            onChange={(e) => setLocalizedValue(key, activeLocale, e.target.value)}
          />
        </div>
      )
    }

    if (type === "richtext") {
      return (
        <div className="w-full">
          <Label>
            {label} ({activeLocale.toUpperCase()})
          </Label>
          <RichTextEditor
            value={cur ?? ""}
            onChange={(html) => setLocalizedValue(key, activeLocale, html)}
            openMedia={openMedia}
          />
        </div>
      )
    }


    if (type === "number") {
      return (
        <div className="w-full">
          <Label>
            {label} ({activeLocale.toUpperCase()})
          </Label>
          <Input
            type="number"
            value={cur ?? ""}
            onChange={(e) => setLocalizedValue(key, activeLocale, e.target.value === "" ? "" : Number(e.target.value))}
          />
        </div>
      )
    }

    if (type === "date") {
      return (
        <div className="w-full">
          <Label>
            {label} ({activeLocale.toUpperCase()})
          </Label>
          <Input
            type="date"
            value={formatDateInput(cur ?? "")}
            onChange={(e) => setLocalizedValue(key, activeLocale, e.target.value)}
          />
        </div>
      )
    }

    if (type === "boolean") {
      return (
        <div className="w-full">
          <Switch
            checked={!!cur}
            onChange={(val) => setLocalizedValue(key, activeLocale, val)}
            label={`${label} (${activeLocale.toUpperCase()})`}
          />
        </div>
      )
    }

    // string (or default)
    return (
      <div className="w-full">
        <Label>
          {label} ({activeLocale.toUpperCase()})
        </Label>
        <Input value={cur ?? ""} onChange={(e) => setLocalizedValue(key, activeLocale, e.target.value)} placeholder={label} />
      </div>
    )
  }

  /* ------------------------------ NON-LOCALIZED ------------------------------ */

  // ROOT MEDIA ✅ (this was missing in your code)
  if (type === "media") {
    const multiple = !!field.multiple
    const urls = multiple ? (Array.isArray(value) ? value : []) : value ? String(value) : ""

    return (
      <div className="w-full">
        <MediaFieldEditor
          label={label}
          value={urls}
          multiple={multiple}
          onPick={() =>
            openMedia({
              mode: "root",
              kind: "media",
              key,
              multiple,
              localized: false,
              locale: null,
            })
          }
          onClear={() => setValue(key, multiple ? [] : "")}
          onRemoveIndex={(rIdx) => {
            if (!multiple) return
            const arr = Array.isArray(value) ? [...value] : []
            arr.splice(rIdx, 1)
            setValue(key, arr)
          }}
        />
      </div>
    )
  }

  if (type === "enum") {
    return (
      <div>
        <Label>{label}</Label>
        <PortalSelect value={value ?? ""} onChange={(e) => setValue(key, e.target.value)}>
          <option value="">Select...</option>
          {(field.values || []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </PortalSelect>
      </div>
    )
  }

  if (type === "json") {
    return <JsonField label={label} value={value} onValue={(v) => setValue(key, v)} />
  }

  if (type === "boolean") {
    return <Switch checked={!!value} onChange={(v) => setValue(key, v)} label={label} />
  }

  if (type === "number") {
    return (
      <div>
        <Label>{label}</Label>
        <Input
          type="number"
          value={value ?? ""}
          onChange={(e) => setValue(key, e.target.value === "" ? "" : Number(e.target.value))}
        />
      </div>
    )
  }

  if (type === "date") {
    return (
      <div>
        <Label>{label}</Label>
        <Input type="date" value={formatDateInput(value)} onChange={(e) => setValue(key, e.target.value)} />
      </div>
    )
  }
  if (type === "text") {
    return (
      <div>
        <Label>{label}</Label>
        <Textarea rows={5} value={value ?? ""} onChange={(e) => setValue(key, e.target.value)} />
      </div>
    )
  }

  if (type === "richtext") {
    return (
      <div>
        <Label>{label}</Label>
        <RichTextEditor
          value={value ?? ""}
          onChange={(html) => setValue(key, html)}
          openMedia={openMedia}
        />
      </div>
    )
  }


  if (type === "link") {
    const v = isObj(value) ? value : emptyLinkValue()

    return (
      <div>
        <Label>{label}</Label>
        <LinkEditor
          label={label}
          value={v}
          withImage={field.link_with_image !== false}
          onChange={(next) => setValue(key, next)}
          onPickImage={() =>
            openMedia({
              mode: "root",
              kind: "link-image",
              key,
              localized: false,
              locale: null,
              multiple: false,
            })
          }
          onClearImage={() => setValue(key, { ...v, image: "" })}
        />
      </div>
    )
  }

  // string / default
  return (
    <div>
      <Label>{label}</Label>
      <Input value={value ?? ""} onChange={(e) => setValue(key, e.target.value)} placeholder={label} />
    </div>
  )
}

/* ✅ SUBFIELD (repeater item) */
function RepeaterSubField({
  parentKey,
  idx,
  field,
  value,
  locales,
  activeLocale,
  setRepeaterItem,
  openMedia,
}) {
  const key = field.key
  const label = field.label || key
  const type = field.type

  const setSubValue = (next) => setRepeaterItem(parentKey, idx, { [key]: next })

  /* ------------------------- LOCALIZED (inside repeater) ------------------------- */
  if (field.localized && type !== "repeater") {
    // localized link
    if (type === "link") {
      const v = ensureLocalizedObject(value, locales, emptyLinkValue())
      const cur = v[activeLocale] || emptyLinkValue()

      return (
        <div className="md:col-span-12">
          <Label>
            {label} ({activeLocale.toUpperCase()})
          </Label>

          <LinkEditor
            label={label}
            value={cur}
            withImage={field.link_with_image !== false}
            onChange={(next) => setSubValue({ ...v, [activeLocale]: next })}
            onPickImage={() =>
              openMedia({
                mode: "repeater",
                kind: "link-image",
                parentKey,
                idx,
                subKey: key,
                multiple: false,
                localized: true,
                locale: activeLocale,
              })
            }
            onClearImage={() =>
              setSubValue({
                ...v,
                [activeLocale]: { ...(v[activeLocale] || emptyLinkValue()), image: "" },
              })
            }
          />
        </div>
      )
    }

    // localized media
    if (type === "media") {
      const multiple = !!field.multiple
      const v = ensureLocalized(value, locales, multiple ? [] : "")
      const cur = v?.[activeLocale] ?? (multiple ? [] : "")

      return (
        <div className="md:col-span-12">
          <MediaFieldEditor
            label={`${label} (${activeLocale.toUpperCase()})`}
            value={cur}
            multiple={multiple}
            onPick={() =>
              openMedia({
                mode: "repeater",
                kind: "media",
                parentKey,
                idx,
                subKey: key,
                multiple,
                localized: true,
                locale: activeLocale,
              })
            }
            onClear={() => setSubValue({ ...v, [activeLocale]: multiple ? [] : "" })}
            onRemoveIndex={(rIdx) => {
              if (!multiple) return
              const arr = Array.isArray(cur) ? [...cur] : []
              arr.splice(rIdx, 1)
              setSubValue({ ...v, [activeLocale]: arr })
            }}
          />
        </div>
      )
    }

    // localized json
    if (type === "json") {
      const v = ensureLocalized(value, locales, [])
      const cur = v?.[activeLocale] ?? []
      return (
        <div className="md:col-span-12">
          <Label>
            {label} ({activeLocale.toUpperCase()})
          </Label>
          <JsonField value={cur} onValue={(val) => setSubValue({ ...v, [activeLocale]: val })} />
        </div>
      )
    }

    // localized enum
    if (type === "enum") {
      const v = ensureLocalized(value, locales, "")
      const cur = v?.[activeLocale] ?? ""
      const options = Array.isArray(field.values) ? field.values : []
      return (
        <div className="md:col-span-4">
          <Label>
            {label} ({activeLocale.toUpperCase()})
          </Label>
          <PortalSelect value={cur} onChange={(e) => setSubValue({ ...v, [activeLocale]: e.target.value })}>
            <option value="">Select...</option>
            {options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </PortalSelect>
        </div>
      )
    }

    // localized simple
    const v = ensureLocalized(value, locales, fallbackForField(field))
    const cur = v?.[activeLocale]

    if (type === "text") {
      return (
        <div className="md:col-span-12">
          <Label>
            {label} ({activeLocale.toUpperCase()})
          </Label>
          <Textarea
            value={cur ?? ""}
            onChange={(e) => setSubValue({ ...v, [activeLocale]: e.target.value })}
            rows={4}
          />
        </div>
      )
    }

    if (type === "richtext") {
      return (
        <div className="md:col-span-12">
          <Label>
            {label} ({activeLocale.toUpperCase()})
          </Label>
          <RichTextEditor
            value={cur ?? ""}
            onChange={(html) => setSubValue({ ...v, [activeLocale]: html })}
            openMedia={openMedia}
          />
        </div>
      )
    }


    if (type === "number") {
      return (
        <div className="md:col-span-3">
          <Label>
            {label} ({activeLocale.toUpperCase()})
          </Label>
          <Input
            type="number"
            value={cur ?? ""}
            onChange={(e) => setSubValue({ ...v, [activeLocale]: e.target.value === "" ? "" : Number(e.target.value) })}
          />
        </div>
      )
    }

    if (type === "date") {
      return (
        <div className="md:col-span-4">
          <Label>
            {label} ({activeLocale.toUpperCase()})
          </Label>
          <Input
            type="date"
            value={formatDateInput(cur ?? "")}
            onChange={(e) => setSubValue({ ...v, [activeLocale]: e.target.value })}
          />
        </div>
      )
    }

    if (type === "boolean") {
      return (
        <div className="md:col-span-4">
          <Switch
            checked={!!cur}
            onChange={(val) => setSubValue({ ...v, [activeLocale]: val })}
            label={`${label} (${activeLocale.toUpperCase()})`}
          />
        </div>
      )
    }

    // string / default
    return (
      <div className="md:col-span-4">
        <Label>
          {label} ({activeLocale.toUpperCase()})
        </Label>
        <Input value={cur ?? ""} onChange={(e) => setSubValue({ ...v, [activeLocale]: e.target.value })} />
      </div>
    )
  }

  /* ------------------------------ NON-LOCALIZED ------------------------------ */

  if (type === "media") {
    const multiple = !!field.multiple
    const urls = multiple ? (Array.isArray(value) ? value : []) : value ? String(value) : ""

    return (
      <div className="md:col-span-12">
        <MediaFieldEditor
          label={label}
          value={urls}
          multiple={multiple}
          onPick={() =>
            openMedia({
              mode: "repeater",
              kind: "media",
              parentKey,
              idx,
              subKey: key,
              multiple,
              localized: false,
              locale: null,
            })
          }
          onClear={() => setSubValue(multiple ? [] : "")}
          onRemoveIndex={(rIdx) => {
            if (!multiple) return
            const arr = Array.isArray(value) ? [...value] : []
            arr.splice(rIdx, 1)
            setSubValue(arr)
          }}
        />
      </div>
    )
  }

  if (type === "link") {
    const v = isObj(value) ? value : emptyLinkValue()
    return (
      <div className="md:col-span-12">
        <Label>{label}</Label>
        <LinkEditor
          label={label}
          value={v}
          withImage={field.link_with_image !== false}
          onChange={(next) => setSubValue(next)}
          onPickImage={() =>
            openMedia({
              mode: "repeater",
              kind: "link-image",
              parentKey,
              idx,
              subKey: key,
              multiple: false,
              localized: false,
              locale: null,
            })
          }
          onClearImage={() => setSubValue({ ...v, image: "" })}
        />
      </div>
    )
  }

  if (type === "enum") {
    const options = Array.isArray(field.values) ? field.values : []
    return (
      <div className="md:col-span-4">
        <Label>{label}</Label>
        <PortalSelect value={value ?? ""} onChange={(e) => setSubValue(e.target.value)} placeholder="Select...">
          <option value="">Select...</option>
          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </PortalSelect>
      </div>
    )
  }

  if (type === "boolean") {
    return (
      <div className="md:col-span-4">
        <Switch checked={!!value} onChange={(v) => setSubValue(v)} label={label} />
      </div>
    )
  }

  if (type === "number") {
    return (
      <div className="md:col-span-3">
        <Label>{label}</Label>
        <Input type="number" value={value ?? ""} onChange={(e) => setSubValue(e.target.value === "" ? "" : Number(e.target.value))} />
      </div>
    )
  }

  if (type === "date") {
    return (
      <div className="md:col-span-4">
        <Label>{label}</Label>
        <Input type="date" value={formatDateInput(value)} onChange={(e) => setSubValue(e.target.value)} />
      </div>
    )
  }

  if (type === "text") {
    return (
      <div className="md:col-span-12">
        <Label>{label}</Label>
        <Textarea
          value={value ?? ""}
          onChange={(e) => setSubValue(e.target.value)}
          rows={4}
        />
      </div>
    )
  }

  if (type === "richtext") {
    return (
      <div className="md:col-span-12">
        <Label>{label}</Label>
        <RichTextEditor
          value={value ?? ""}
          onChange={(html) => setSubValue(html)}
          openMedia={openMedia}
        />
      </div>
    )
  }


  if (type === "json") {
    return (
      <div className="md:col-span-12">
        <Label>{label}</Label>
        <JsonField value={value} onValue={(v) => setSubValue(v)} />
      </div>
    )
  }

  // string / default
  return (
    <div className="md:col-span-4">
      <Label>{label}</Label>
      <Input value={value ?? ""} onChange={(e) => setSubValue(e.target.value)} />
    </div>
  )
}
